import { RefObject, useMemo } from "react";
import { useAppSelector, useAppParams } from "@app/hooks";
import { selectCurrentUser } from "@app/features/auth/authSlice";
import { useHybridLockStatus } from "@app/hooks/useHybridLockStatus";
import { selectIsMapSaveInProgress } from "@app/features/mapSaveStatus/mapSaveStatusSlice";
import MapLockOverlay from "./MapLockOverlay";

interface BintactMapProps {
  iframeRef: RefObject<HTMLIFrameElement>;
  mapUrl: string;
}

const useBintactMapLock = () => {
  // Get lock status and current user
  const { lockInfo } = useHybridLockStatus();
  const currentUser = useAppSelector(selectCurrentUser);

  // Calculate if map should be locked
  const isLockedByOther = useMemo(() => {
    return !!(lockInfo?.is_locked && lockInfo?.locked_by !== currentUser?.email);
  }, [lockInfo?.is_locked, lockInfo?.locked_by, currentUser?.email]);

  const isMapLocked = useMemo(() => {
    return isLockedByOther;
  }, [isLockedByOther]);

  // Determine lock message
  const lockMessageConfig = useMemo(() => {
    if (isLockedByOther && lockInfo?.locked_by) {
      return {
        message: undefined,
        translationKey: "project.project_locked_by",
        translationParams: { user: lockInfo.locked_by },
      };
    }
    return {
      message: undefined,
      translationKey: undefined,
      translationParams: undefined,
    };
  }, [isLockedByOther, lockInfo?.locked_by]);

  return {
    isMapLocked,
    lockMessage: lockMessageConfig.message,
    lockTranslationKey: lockMessageConfig.translationKey,
    lockTranslationParams: lockMessageConfig.translationParams,
  };
};

const BintactMap = ({ iframeRef, mapUrl }: BintactMapProps) => {
  const { projectId } = useAppParams();
  const {
    isMapLocked,
    lockMessage,
    lockTranslationKey,
    lockTranslationParams,
  } = useBintactMapLock();
  const isMapSaveInProgress = useAppSelector((state) =>
    projectId ? selectIsMapSaveInProgress(state, projectId) : false,
  );

  const isMapBlocked = isMapLocked || isMapSaveInProgress;

  const overlayConfig = useMemo(() => {
    if (isMapSaveInProgress) {
      return {
        message: "Save in progress. The map cannot be edited until the save completes.",
        translationKey: "map.save_in_progress" as const,
        translationParams: undefined as Record<string, string> | undefined,
        subtitleTranslationKey: "map.save_in_progress_hint" as const,
        subtitleTranslationParams: undefined as Record<string, string> | undefined,
      };
    }
    if (isMapLocked) {
      return {
        message: lockMessage || "Map is locked",
        translationKey: lockTranslationKey,
        translationParams: lockTranslationParams,
        subtitleTranslationKey: undefined,
        subtitleTranslationParams: undefined,
      };
    }
    return null;
  }, [
    isMapSaveInProgress,
    isMapLocked,
    lockMessage,
    lockTranslationKey,
    lockTranslationParams,
  ]);

  const iframeStyle = useMemo(
    () => ({
      width: "min(100%, calc(100vw - 140px))",
      maxWidth: "100%",
      maxHeight: "min(calc(100vh - 150px), calc(100vw - 400px), 1400px)",
      border: "none",
      aspectRatio: "1",
    }),
    []
  );

  return (
    <div
      className="map-iframe-container"
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        padding: "0 2rem 1rem 2rem",
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      <div
        className={isMapBlocked ? "map-locked" : ""}
        style={{
          width: "100%",
          height: "100%",
          maxWidth: "100%",
          maxHeight: "100%",
          filter: isMapBlocked ? "blur(4px)" : "none",
          transition: "filter 0.3s ease",
        }}
      >
        {isMapBlocked ? (
          <div
            style={{
              ...iframeStyle,
              backgroundColor: "var(--bg-secondary, #f5f5f5)",
            }}
          />
        ) : (
          <iframe
            ref={iframeRef}
            src={mapUrl}
            style={{
              ...iframeStyle,
              pointerEvents: "auto",
            }}
            title="FAO Ex-Act Map"
            allowFullScreen
          />
        )}
      </div>
      {overlayConfig && (
        <MapLockOverlay
          message={overlayConfig.message}
          translationKey={overlayConfig.translationKey}
          translationParams={overlayConfig.translationParams}
          subtitleTranslationKey={overlayConfig.subtitleTranslationKey}
          subtitleTranslationParams={overlayConfig.subtitleTranslationParams}
        />
      )}
    </div>
  );
};

export default BintactMap;